/**
 * 哈希路由骨架（T01）：按 `location.hash` 渲染路由表里对应的页面。
 *
 * 纯逻辑（解析 / 订阅 / 跳转）在 `hashRoute.ts`，本文件只做「渲染当前页面」。
 *
 * T06 起在这里接入页面切换动效：直接引用 `src/design/motion.ts` 的冻结预设
 * `pageTransition`（主 tab 按导航位置完整左右滑动，独立页按前进 / 后退层级切换），
 * 由 `AnimatePresence` 将最新方向传给退出页面；独立页保留 28% 视差退让。
 * `prefers-reduced-motion` 时同一预设自动降级为淡入淡出，
 * 本文件不新增任何弹簧参数。
 *
 * 除 `routes.ts` 登记的四条主路由外，还有若干「独立页」不占底部导航，只在路由层判定：
 * `#/attachments`（T09 附件演示）、`#/memory`（T13 记忆管理）、`#/providers`（T15 提供商配置）、
 * `#/community`（T07b 角色卡社区）、`#/gallery`（T11b 图库与存图）。
 *
 * 主线收口说明：以上独立页分别由不同任务分支加入，合并时统一改为下面这张表驱动，
 * 避免每加一个独立页就在本文件里再复制一段三元表达式。
 */
import type { ReactElement } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

import { useMotionPresets } from '../design/motion';
import { AttachmentsPage } from '../features/attachments/AttachmentsPage';
import { COMMUNITY_PATH, CommunityPage } from '../features/community';
import { MemoryPage } from '../features/memory/MemoryPage';
import { PROVIDERS_PATH, ProvidersPage } from '../features/providers';
import { GalleryPage } from '../features/gallery/GalleryPage';
import { AccountPage } from '../features/account';
import { BillingPage } from '../features/billing';
import { MarketplacePage } from '../features/marketplace';
import { TtsPage } from '../features/mimo/TtsPage';
import { AdminPage } from '../features/admin/AdminPage';
import { ChatPage } from '../features/chat/ChatPage';
import { useNavigation, isStandalonePath, STANDALONE_TITLES } from './hashRoute';
import type { StandalonePath } from './hashRoute';
import { matchRoute } from './routes';

/** T09 附件演示页路径（独立于底部导航的四条主路由）。*/
export const ATTACHMENTS_PATH = '/attachments';

/** T13 记忆管理页路径（同样独立于底部导航的四条主路由）。 */
export const MEMORY_PATH = '/memory';

/** T11b 图库页路径（同样独立于底部导航的四条主路由）。 */
export const GALLERY_PATH = '/gallery';

/** 独立页表：路径 -> 页面组件。新增独立页只需在此追加一项。 */
const STANDALONE_ROUTES: readonly {
  path: StandalonePath;
  Component: () => ReactElement;
}[] = [
  { path: ATTACHMENTS_PATH, Component: AttachmentsPage },
  { path: MEMORY_PATH, Component: MemoryPage },
  { path: PROVIDERS_PATH, Component: ProvidersPage },
  { path: COMMUNITY_PATH, Component: CommunityPage },
  { path: GALLERY_PATH, Component: GalleryPage },
  { path: '/account', Component: AccountPage },
  { path: '/marketplace', Component: MarketplacePage },
  { path: '/billing', Component: BillingPage },
  { path: '/tts', Component: () => <TtsPage /> },
  { path: '/admin', Component: AdminPage },
  { path: '/chat', Component: ChatPage },
];

/** 渲染当前 hash 对应的页面；未知 hash 落到默认路由并把地址栏规范化。*/
export function HashRouter(): ReactElement {
  const { path, direction } = useNavigation();
  const presets = useMotionPresets();
  const standalone = STANDALONE_ROUTES.find((entry) => entry.path === path);

  const route = matchRoute(path);
  const pageKey = path;
  const StandaloneComponent = standalone?.Component;

  return (
    <AnimatePresence initial={false} custom={direction}>
      <motion.div
        key={pageKey}
        custom={direction}
        variants={presets.pageTransition}
        initial="enter"
        animate="center"
        exit="exit"
        data-navigation-direction={direction}
        data-navigation-path={path}
        style={{ position: 'absolute', inset: 0 }}
      >
        {StandaloneComponent ? (
          <StandaloneComponent />
        ) : isStandalonePath(path) ? (
          // Reserved feature hook: register its component above when the worker delivers it.
          <h1>{STANDALONE_TITLES[path]}</h1>
        ) : (
          <route.Component />
        )}
      </motion.div>
    </AnimatePresence>
  );
}
